const MAX_PROVENANCE_DETAIL_BINDINGS = 4_096;
const collectors = new WeakMap();
const detailsByElement = new WeakMap();

function collectorState(collector) {
    const state = collectors.get(collector);
    if (!state) {
        throw new TypeError("detailBindings must come from createProvenanceDetailBindings()");
    }
    if (state.consumed) {
        throw new TypeError("provenance detail bindings have already been consumed");
    }
    return state;
}

function elementBindingKey(element) {
    return String(
        element?.dataset?.fishyProvenanceBinding
        ?? element?.getAttribute?.("data-fishy-provenance-binding")
        ?? "",
    ).trim();
}

/**
 * Creates one opaque detail collector for a single light-DOM render.
 *
 * The collector intentionally exposes no state. It holds exact provenance
 * claim records only until bindProvenanceDetailBindings() consumes the render.
 */
export function createProvenanceDetailBindings() {
    const collector = Object.freeze({});
    collectors.set(collector, {
        consumed: false,
        entries: new Map(),
        nextKey: 0,
    });
    return collector;
}

export function validateProvenanceDetailBindings(collector) {
    collectorState(collector);
}

export function collectProvenanceDetailBinding(collector, claims) {
    const state = collectorState(collector);
    if (state.entries.size >= MAX_PROVENANCE_DETAIL_BINDINGS) {
        throw new RangeError(
            `provenance detail bindings cannot exceed ${MAX_PROVENANCE_DETAIL_BINDINGS} segments`,
        );
    }
    const key = `p${state.nextKey.toString(36)}`;
    state.nextKey += 1;
    state.entries.set(key, Object.freeze({ claims }));
    return key;
}

/**
 * Resolves one render's short DOM keys into exact provenance records.
 *
 * Consumption clears every strong reference even when DOM matching fails.
 * The records then live only behind their DOM elements, so replacing the
 * rendered subtree makes both nodes and details eligible for collection.
 */
export function bindProvenanceDetailBindings(root, collector) {
    if (!root || typeof root.querySelectorAll !== "function") {
        throw new TypeError("bindProvenanceDetailBindings requires a queryable root");
    }
    const state = collectorState(collector);
    const selector = "[data-fishy-provenance-binding]";
    let boundCount = 0;
    try {
        const elements = new Set();
        if (typeof root.matches === "function" && root.matches(selector)) {
            elements.add(root);
        }
        for (const element of root.querySelectorAll(selector)) {
            elements.add(element);
        }
        for (const element of elements) {
            const details = state.entries.get(elementBindingKey(element));
            if (!details || !element || typeof element !== "object") {
                continue;
            }
            detailsByElement.set(element, details);
            boundCount += 1;
        }
    } finally {
        state.entries.clear();
        state.consumed = true;
    }
    return boundCount;
}

export function provenanceDetailsForElement(element) {
    if (!element || (typeof element !== "object" && typeof element !== "function")) {
        return null;
    }
    return detailsByElement.get(element) ?? null;
}
